import { useState } from "react";
import { Link } from "react-router-dom";
import { Palette, Sparkles, Download, Loader2, ArrowLeft, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BubbleLetterGenerator = () => {
  const [text, setText] = useState("");
  const [theme, setTheme] = useState("classic");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generationsLeft, setGenerationsLeft] = useState(3);

  const themes = [
    { id: "classic", label: "Classic Bubble", desc: "Round puffy letters" },
    { id: "graffiti", label: "Graffiti Style", desc: "Street art inspired" },
    { id: "3d", label: "3D Effect", desc: "Letters with depth" },
    { id: "cute", label: "Cute & Kawaii", desc: "Adorable faces on letters" }
  ];

  const handleGenerate = async () => {
    if (!text.trim()) {
      toast.error("Please enter some text");
      return;
    }

    if (generationsLeft <= 0) {
      toast.error("You've used all free generations. Sign up for more!");
      return;
    }

    setIsGenerating(true);
    
    try {
      let prompt = "";
      switch (theme) {
        case "classic":
          prompt = `Coloring page with "${text.toUpperCase()}" in classic round bubble letters, puffy and inflated style, black and white line art for coloring, fun for kids`;
          break;
        case "graffiti":
          prompt = `Coloring page with "${text.toUpperCase()}" in graffiti bubble letters style, street art inspired, with arrows and stars, black and white line art for coloring`;
          break;
        case "3d":
          prompt = `Coloring page with "${text.toUpperCase()}" in 3D bubble letters with shadow and depth effect, black and white line art for coloring`;
          break;
        case "cute":
          prompt = `Coloring page with "${text.toUpperCase()}" in kawaii bubble letters with cute faces on each letter, adorable expressions, black and white line art for coloring`;
          break;
        default:
          prompt = `Coloring page with "${text.toUpperCase()}" in bubble letters, black and white line art for coloring`;
      }

      const response = await axios.post(`${BACKEND_URL}/api/generate/free-tool`, {
        prompt: prompt,
        tool_type: "bubble_letter_generator"
      });

      if (response.data.image_url) {
        setGeneratedImage(response.data.image_url);
        setGenerationsLeft(prev => prev - 1);
        toast.success("Bubble letters generated!");
      }
    } catch (error) {
      console.error("Generation error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please try again later or sign up!");
      } else {
        toast.error("Failed to generate. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement("a");
      link.href = generatedImage;
      link.download = `bubble-letters-${text}.png`;
      link.click();
      toast.success("Download started!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-violet-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/free-tools" className="text-slate-600 hover:text-slate-900 font-medium hidden md:flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                All Tools
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/free-tools" className="inline-flex items-center gap-2 text-pink-600 hover:text-pink-700 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Free Tools
            </Link>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-pink-600" />
              <span className="text-sm font-medium text-pink-700">Free Bubble Letter Generator</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Bubble Letter Generator
            </h1>
            
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Transform any word into fun bubble letters! Perfect for learning the alphabet, 
              party decorations, or creative coloring activities.
            </p>
          </div>

          {/* Generator Card */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Create Your Letters</h2>
              
              {/* Text Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter Text
                </label>
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value.toUpperCase())}
                  placeholder="e.g., HELLO, ABC, LOVE"
                  maxLength={10}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-lg uppercase"
                  data-testid="text-input"
                />
                <p className="text-xs text-slate-500 mt-1">{text.length}/10 characters • Letters only work best</p>
              </div>

              {/* Theme Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Choose Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {themes.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        theme === t.id
                          ? "border-pink-500 bg-pink-50"
                          : "border-slate-200 hover:border-pink-200"
                      }`}
                      data-testid={`theme-${t.id}`}
                    >
                      <div className="font-medium text-slate-800 text-sm">{t.label}</div>
                      <div className="text-xs text-slate-500">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !text.trim()}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-semibold text-lg disabled:opacity-50"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Bubbles...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Bubble Letters
                  </>
                )}
              </Button>

              {/* Generations Counter */}
              <div className="mt-4 text-center">
                <span className="text-sm text-slate-500">
                  {generationsLeft} free generations remaining
                </span>
                {generationsLeft <= 1 && (
                  <Link to="/signup" className="block text-pink-600 text-sm font-medium mt-1 hover:underline">
                    Sign up for unlimited generations →
                  </Link>
                )}
              </div>
            </div>

            {/* Preview Section */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Preview</h2>
              
              <div className="aspect-square rounded-xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
                {generatedImage ? (
                  <img
                    src={generatedImage}
                    alt={`${text} bubble letters`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-8">
                    <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Your bubble letters will appear here</p>
                    <p className="text-sm text-slate-400 mt-2">Enter text and click generate</p>
                  </div>
                )}
              </div>

              {/* Download Button */}
              {generatedImage && (
                <Button
                  onClick={handleDownload}
                  className="w-full h-12 mt-4 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
                  data-testid="download-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Free
                </Button>
              )}
            </div>
          </div>

          {/* Use Cases Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Great For
            </h2>
            
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { title: "Learning ABCs", desc: "Help kids learn the alphabet through coloring" },
                { title: "Party Banners", desc: "Create custom decorations for any celebration" },
                { title: "Name Art", desc: "Make personalized artwork with names" },
                { title: "Classroom Use", desc: "Educational activities for teachers" }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center mb-3 mx-auto">
                    <Check className="w-5 h-5 text-pink-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1 text-sm">{item.title}</h3>
                  <p className="text-slate-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-pink-500 to-rose-600 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Create Any Coloring Page You Can Imagine
            </h2>
            <p className="text-pink-100 mb-6 max-w-xl mx-auto">
              Bubble letters are just the beginning! With ColorWonder, 
              generate unlimited custom coloring pages of anything.
            </p>
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-14 px-8 rounded-full bg-white text-pink-600 font-semibold text-lg hover:bg-pink-50"
            >
              Try Full Generator Free →
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 ColorWonder. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/free-tools" className="hover:text-slate-700">Free Tools</Link>
            <Link to="/blog" className="hover:text-slate-700">Blog</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BubbleLetterGenerator;
